import React, { createContext, useContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { SESSAO_FALSA_ATIVA, sessaoFalsa, deslogadoFalso } from '../dev/sessaoFalsa';
import { MODO_DEMO, sessaoDaDemo, SAIDA_DA_DEMO } from '../demo/modo';
import { GENERICA, mensagemDeAuth } from '../utils/erroDeAuth';
import { CAMINHO_DO_CONVITE_DE_META, conviteDeMetaPendente } from '../utils/conviteDeMeta';
import { rotaDeConsentimento } from '../utils/rotaPublica';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Email e senha (05/10). As quatro devolvem `null` quando deu certo, ou a
   * frase em português para mostrar — nunca lançam, e nunca devolvem o erro
   * cru do Supabase. Ver `utils/erroDeAuth.ts`.
   */
  entrarComSenha: (email: string, senha: string) => Promise<string | null>;
  criarConta: (p: { nome: string; email: string; senha: string }) => Promise<string | null>;
  pedirNovaSenha: (email: string) => Promise<string | null>;
  definirSenha: (senha: string) => Promise<string | null>;
  /**
   * `true` quando ela chegou pelo link de "esqueci minha senha": existe sessão,
   * mas a única coisa a fazer agora é escolher a senha nova.
   */
  recuperandoSenha: boolean;
}

/**
 * Para onde o link do email traz de volta.
 *
 * A origem, mais o caminho do convite de meta quando há um em aberto. O
 * convite atravessa o login do Google guardado no `sessionStorage` da aba —
 * mas um email de confirmação costuma ser aberto em OUTRO aparelho, onde essa
 * aba não existe. Com o caminho no link, o convite chega junto.
 *
 * Exige que o Supabase aceite `https://fintracking.com.br/**` nos endereços
 * de retorno; sem isso ele ignora o caminho e volta para a raiz.
 */
const retornoDoEmail = (): string => {
  /*
   * Quem cria a conta pela tela de autorização do conector volta PARA ELA,
   * com o pedido na URL — antes voltava para a raiz e precisava conectar de
   * novo pelo aplicativo de IA. O pedido vale por pouco tempo: se já tiver
   * vencido, a própria tela diz e manda reconectar.
   */
  if (rotaDeConsentimento(window.location.pathname)) {
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  }
  const convite = conviteDeMetaPendente();
  return convite
    ? `${window.location.origin}/${CAMINHO_DO_CONVITE_DE_META}/${convite}`
    : window.location.origin;
};

/** A sessão de desenvolvimento não fala com o Supabase: finge que deu certo. */
const fingir = async (): Promise<string | null> => {
  await new Promise(r => setTimeout(r, 500));
  return null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recuperandoSenha, setRecuperandoSenha] = useState(false);

  useEffect(() => {
    /*
     * A sessão de desenvolvimento entra já pronta, sem ida ao Supabase.
     *
     * Em produção `SESSAO_FALSA_ATIVA` é constante falsa e este ramo sai do
     * arquivo na build — ver `dev/sessaoFalsa.ts`.
     */
    /*
     * A demonstração (`/demo`) entra pronta, com uma pessoa inventada, e não
     * consulta o serviço de login — nem para saber se quem abriu tem conta.
     * A sessão de verdade dela, se existir, fica intacta do lado de fora.
     */
    if (MODO_DEMO) {
      setSession(sessaoDaDemo);
      setLoading(false);
      return;
    }

    if (SESSAO_FALSA_ATIVA) {
      // `dev-deslogado=1` mostra a tela de LOGIN, que é a única que a sessão
      // falsa não alcança — ver a nota em `dev/sessaoFalsa.ts`.
      setSession(deslogadoFalso() ? null : sessaoFalsa);
      // `dev-recuperando=1` mostra a tela de escolher a senha nova, que no app
      // de verdade só abre vindo do link do email.
      try { setRecuperandoSenha(localStorage.getItem('dev-recuperando') === '1'); } catch { /* sem storage */ }
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      /*
       * O link de "esqueci minha senha" entra com uma sessão de verdade. Sem
       * marcar, o app abriria normalmente e ela nunca veria onde trocar a
       * senha — que foi o motivo de ter clicado no link.
       */
      if (event === 'PASSWORD_RECOVERY') setRecuperandoSenha(true);
      if (event === 'SIGNED_OUT') setRecuperandoSenha(false);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  };

  const signOut = async () => {
    // Sair da demonstração é sair do endereço dela. Chamar o serviço de login
    // aqui encerraria a sessão DE VERDADE de quem só estava olhando.
    if (MODO_DEMO) { window.location.assign(SAIDA_DA_DEMO); return; }
    await supabase.auth.signOut();
  };

  const entrarComSenha = async (email: string, senha: string): Promise<string | null> => {
    if (SESSAO_FALSA_ATIVA) return fingir();
    try {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: senha });
      return error ? mensagemDeAuth(error) : null;
    } catch {
      return GENERICA;
    }
  };

  /*
   * Cadastro. O nome vai em `full_name`, o mesmo campo que o Google preenche:
   * o resto do app lê dali e não precisa saber por onde ela entrou.
   *
   * Quando o email JÁ tem conta, o Supabase responde como se tivesse criado e
   * não manda email nenhum — é o que impede descobrir quem usa o app. Por
   * isso `null` aqui quer dizer "pedido aceito", e a tela seguinte fala dos
   * dois casos.
   */
  const criarConta = async (p: { nome: string; email: string; senha: string }): Promise<string | null> => {
    if (SESSAO_FALSA_ATIVA) return fingir();
    try {
      const { error } = await supabase.auth.signUp({
        email: p.email.trim(),
        password: p.senha,
        options: { data: { full_name: p.nome.trim() }, emailRedirectTo: retornoDoEmail() },
      });
      return error ? mensagemDeAuth(error) : null;
    } catch {
      return GENERICA;
    }
  };

  const pedirNovaSenha = async (email: string): Promise<string | null> => {
    if (SESSAO_FALSA_ATIVA) return fingir();
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: retornoDoEmail() });
      return error ? mensagemDeAuth(error) : null;
    } catch {
      return GENERICA;
    }
  };

  const definirSenha = async (senha: string): Promise<string | null> => {
    if (SESSAO_FALSA_ATIVA) { await fingir(); setRecuperandoSenha(false); return null; }
    try {
      const { error } = await supabase.auth.updateUser({ password: senha });
      if (error) return mensagemDeAuth(error);
      setRecuperandoSenha(false);
      return null;
    } catch {
      return GENERICA;
    }
  };

  return (
    <AuthContext.Provider value={{
      session, user: session?.user ?? null, loading, signInWithGoogle, signOut,
      entrarComSenha, criarConta, pedirNovaSenha, definirSenha, recuperandoSenha,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
